import styled from 'astroturf/react';
import React, { ComponentType, ReactNode, Suspense } from 'react';

import { suspendForPageLang } from 'js/intl/t';
import { isRtlLocale } from 'js/utils/rtl';

export interface PageLangProps {
  // The visitor's lang, resolved on the server; absent or 'en' renders English.
  pageLang?: string;
}

// On window, not module state: a modal can mount from a chunk with its own copy of this module
// (liswp_blocks/AGENTS.md), and window is the one channel every copy shares.
const PUBLISHED_KEY = '__aeduPageLang';
type PublishedWindow = Window & { [PUBLISHED_KEY]?: string };

function PageLangGate({
  lang,
  children,
}: {
  lang: string;
  children: ReactNode;
}): JSX.Element {
  suspendForPageLang(lang);
  if (typeof window !== 'undefined') {
    (window as PublishedWindow)[PUBLISHED_KEY] = lang;
  }
  return <>{children}</>;
}

// Boxless, so wrapping a block doesn't change its layout.
export const LangScope = styled.div`
  display: contents;
`;

function InLang({
  lang,
  children,
}: {
  lang: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <LangScope lang={lang} dir={isRtlLocale(lang) ? 'rtl' : 'ltr'}>
      <Suspense fallback={null}>
        <PageLangGate lang={lang}>{children}</PageLangGate>
      </Suspense>
    </LangScope>
  );
}

/**
 * Renders Component in props.pageLang. The same tree renders on the server (renderWithPageLang)
 * and hydrates on the client, which waits for the lang's bundle inside Suspense: hydration keeps
 * the server's translated HTML until then, so there is no English flash and no mismatch.
 */
export function withPageLang<P extends object>(
  Component: ComponentType<P>,
): ComponentType<P & PageLangProps> {
  const Localized = (props: P & PageLangProps): JSX.Element => {
    const lang = props.pageLang ?? 'en';
    // English never suspends, so it renders the bare component's HTML.
    if (lang === 'en') {
      return (
        <PageLangGate lang={lang}>
          <Component {...props} />
        </PageLangGate>
      );
    }
    return (
      <InLang lang={lang}>
        <Component {...props} />
      </InLang>
    );
  };
  Localized.displayName = `withPageLang(${Component.displayName ?? Component.name})`;
  return Localized;
}

/**
 * Renders a tree mounted outside the page's (a modal, via renderModal) in the lang a withPageLang
 * component published; a page without one renders it untouched.
 */
export function InPublishedPageLang({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const lang =
    typeof window === 'undefined'
      ? undefined
      : (window as PublishedWindow)[PUBLISHED_KEY];
  if (!lang || lang === 'en') return <>{children}</>;
  return <InLang lang={lang}>{children}</InLang>;
}
